import React, { useCallback, useState } from "react";
import { SocialMediaPost } from "@/src/types/user";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/src/components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";
import { Badge } from "@/src/components/ui/badge";
import { formatDateTime } from "@/src/lib/utils";

interface SocialMediaPostTableProps {
  posts: SocialMediaPost[];
}

export function SocialMediaPostTable({ posts }: SocialMediaPostTableProps) {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const getBadgeVariant = useCallback((status: string) => {
    switch (status) {
      case "approved":
        return "default";
      case "pending":
        return "outline";
      case "rejected":
        return "destructive";
      default:
        return "secondary";
    }
  }, []);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Social Media Posts</CardTitle>
        </CardHeader>
        <CardContent>
          {posts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Social Media</TableHead>
                  <TableHead className="min-w-36 text-center">Status</TableHead>
                  <TableHead>Link</TableHead>
                  <TableHead>Posted At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((post: SocialMediaPost, index) => (
                  <TableRow key={`social-media-post-${index}`}>
                    <TableCell className="capitalize">
                      {post.socialMedia}
                    </TableCell>
                    <TableCell className="text-center align-middle">
                      <Badge
                        variant={getBadgeVariant(post.status)}
                        className="capitalize"
                      >
                        {post.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <img
                        src={post.link}
                        alt={`${post.socialMedia} post`}
                        className="h-12 w-12 object-cover object-center rounded cursor-pointer border border-gray-200 hover:opacity-80 transition-opacity"
                        onClick={() => setSelectedImage(post.link)}
                      />
                    </TableCell>
                    <TableCell>{formatDateTime(post.postedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-4">No social media posts found</div>
          )}
        </CardContent>
      </Card>

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center cursor-pointer"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <img
              src={selectedImage}
              alt="Full size preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <button
              className="absolute top-2 right-2 bg-white/80 hover:bg-white rounded-full p-1.5 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
